import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { adminService } from '../../services/api';
import { Fournisseur, PaginatedResponse } from '../../types';
import DataTable from '../common/DataTable';
import LoadingSpinner from '../common/LoadingSpinner';
import FournisseurForm from './FournisseurForm';

interface FournisseurManagementProps {
  isVisible: boolean;
}

const FournisseurManagement: React.FC<FournisseurManagementProps> = ({ isVisible }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingFournisseur, setEditingFournisseur] = useState<Fournisseur | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const queryClient = useQueryClient();

  const { data: fournisseursData, isLoading } = useQuery<PaginatedResponse<Fournisseur>>({
    queryKey: ['admin-fournisseurs', searchTerm],
    keepPreviousData: true,
    queryFn: () => adminService.getFournisseurs({ search: searchTerm }).then(res => res.data),
    enabled: isVisible,
  });

  const createFournisseurMutation = useMutation({
    mutationFn: (data: Omit<Fournisseur, 'id'>) => adminService.createFournisseur(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-fournisseurs'] });
      setShowForm(false);
      setEditingFournisseur(null);
    },
  });

  const updateFournisseurMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Fournisseur, 'id'>> }) =>
      adminService.updateFournisseur(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-fournisseurs'] });
      setShowForm(false);
      setEditingFournisseur(null);
    },
  });

  const deleteFournisseurMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteFournisseur(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-fournisseurs'] });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => adminService.toggleFournisseurStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-fournisseurs'] });
    },
  });

  const handleSubmit = (data: Omit<Fournisseur, 'id'>) => {
    if (editingFournisseur) {
      updateFournisseurMutation.mutate({ id: editingFournisseur.id, data });
    } else {
      createFournisseurMutation.mutate(data);
    }
  };

  const handleEdit = (fournisseur: Fournisseur) => {
    setEditingFournisseur(fournisseur);
    setShowForm(true);
  };

  const handleDelete = (fournisseur: Fournisseur) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le fournisseur "${fournisseur.nom}" ?`)) {
      deleteFournisseurMutation.mutate(fournisseur.id);
    }
  };

  const handleToggleStatus = (fournisseur: Fournisseur) => {
    toggleStatusMutation.mutate(fournisseur.id);
  };

  const columns = [
    {
      key: 'nom',
      label: 'Nom',
    },
    {
      key: 'Contact',
      label: 'contact',
      render: (_: any, fournisseur: Fournisseur) => fournisseur.contact || '-',
    },
    {
      key: 'Téléphone',
      label: 'telephone',
      render: (_: any, fournisseur: Fournisseur) => fournisseur.telephone || '-',
    },
    {
      key: 'Email',
      label: 'email',
      render: (_: any, fournisseur: Fournisseur) => fournisseur.email || '-',
    },
    {
      key: 'Adresse',
      label: 'adresse',
      render: (_: any, fournisseur: Fournisseur) => fournisseur.adresse || '-',
    },
    {
      key: 'Statut',
      label: 'is_active',
      render: (_: any, fournisseur: Fournisseur) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          fournisseur.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {fournisseur.is_active ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      key: 'Actions',
      label: 'actions',
      sortable: false,
      render: (_: any, fournisseur: Fournisseur) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(fournisseur)}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(fournisseur)}
            className={`${fournisseur.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
            title={fournisseur.is_active ? 'Désactiver' : 'Activer'}
          >
            {fournisseur.is_active ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => handleDelete(fournisseur)}
            className="text-red-600 hover:text-red-900"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (!isVisible) return null;

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Gestion des fournisseurs</h2>
          <p className="mt-1 text-sm text-gray-500">
            Gérez votre réseau de fournisseurs
          </p>
        </div>
        <button
          onClick={() => {
            setEditingFournisseur(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouveau fournisseur
        </button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Rechercher un fournisseur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
        </div>
      </div>

      <DataTable
        data={fournisseursData?.results || []}
        columns={columns}
        loading={isLoading}
      />

      {showForm && (
        <FournisseurForm
          fournisseur={editingFournisseur}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingFournisseur(null);
          }}
          loading={createFournisseurMutation.isPending || updateFournisseurMutation.isPending}
        />
      )}
    </div>
  );
};

export default FournisseurManagement;